// The lesson player: a focused, one-thing-at-a-time flow.
// chapter opening (first lesson only) -> intro -> verses (or story cards)
// -> quick check -> reflection -> lesson complete.
import { html, paragraphs } from '../lib/html.js';
import { icon } from '../lib/icons.js';
import { getProgress, actions, noteFor } from '../lib/store.js';
import {
  getIndex, lesson as getLesson, chapter, loadChapter, cachedChapter, chapterSky, chapterStats,
  verseIdsForLesson, nextLessonAfter, lessonMinutes, person, overallStats,
} from '../lib/content.js';
import { back, navigate } from '../lib/router.js';
import { streaks } from '../../shared/progress.js';
import { plural } from '../lib/format.js';
import { verseBlock } from '../components/verse.js';
import { avatar } from '../components/chrome.js';
import { dawnScene, skyDecor } from '../components/dawn.js';
import { toast } from '../components/toast.js';
import { openPersonSheet } from '../components/actions.js';
import { saveCard, bindSaveCard } from '../components/save-card.js';
import { stop as stopSpeech } from '../lib/speech.js';
import notfound from './notfound.js';

function buildSteps(l, extra) {
  const steps = [];
  if (l.ch > 0 && l.isFirst) steps.push({ type: 'opening' });
  steps.push({ type: 'intro' });
  if (l.ch === 0) l.cards.forEach((card, i) => steps.push({ type: 'card', card, i }));
  else for (let v = l.from; v <= l.to; v++) steps.push({ type: 'verse', v });
  extra.quiz.forEach((q, i) => steps.push({ type: 'quiz', q, i }));
  steps.push({ type: 'reflect' });
  steps.push({ type: 'done' });
  return steps;
}

export default async function lessonView({ id }) {
  const l = getLesson(id);
  if (!l) return notfound();
  const idx = getIndex();
  const isPrologue = l.ch === 0;
  const meta = isPrologue ? idx.prologue : chapter(l.ch);
  const data = isPrologue ? null : await loadChapter(l.ch);
  const extra = isPrologue ? l : data.lessonExtras.get(l.id);
  if (isPrologue) {
    const chs = [...new Set(l.cards.filter((c) => c.verse).map((c) => Number(c.verse.split('.')[0])))];
    await Promise.all(chs.map((c) => loadChapter(c)));
  }
  const sky = chapterSky(l.ch);
  const steps = buildSteps(l, extra);
  const progress = getProgress();
  const wasComplete = !!progress.lessons[l.id];
  let index = 0;
  if (!wasComplete && progress.current && progress.current.lesson === l.id) {
    index = Math.min(Math.max(0, progress.current.step), steps.length - 2);
  }
  const answers = new Map();
  const verseCount = isPrologue ? 0 : l.to - l.from + 1;
  const quizTotal = extra.quiz.length;
  const lessonsInChapter = meta.lessons.length;
  let completedNow = false;

  const where = isPrologue ? `Background · Lesson ${l.n} of 4` : `Chapter ${l.ch} · Lesson ${l.n} of ${lessonsInChapter}`;

  // ---------- step renderers ----------
  const R = {
    opening() {
      return {
        html: html`<div class="opening">
          ${skyDecor(sky.f)}
          <p class="eyebrow">Chapter ${l.ch} of 18 begins</p>
          <p class="opening__sa deva" lang="sa">${meta.sa}</p>
          <h2 class="display step__heading" tabindex="-1">${meta.title}</h2>
          <p class="opening__name"><span class="iast" lang="sa-Latn">${meta.iast}</span><span aria-hidden="true"> · </span>${meta.nameMeaning}</p>
          <p class="opening__big">${meta.bigIdea}</p>
          <div class="opening__story"><p class="eyebrow">Where we are</p><p>${meta.storySoFar}</p></div>
        </div>`,
        primary: 'Begin the chapter',
      };
    },
    intro() {
      const range = isPrologue ? `${l.cards.length} short cards` : l.from === l.to ? `Verse ${l.from}` : `Verses ${l.from} to ${l.to}`;
      return {
        html: html`<div class="intro">
          <p class="eyebrow">${where}</p>
          <h2 class="display step__heading" tabindex="-1">${l.title}</h2>
          <p class="intro__idea">${l.idea}</p>
          <p class="intro__text">${l.intro}</p>
          <p class="intro__meta">${icon('clock', { size: 16 })}<span>About ${lessonMinutes(l)} min</span><span aria-hidden="true">·</span><span>${range}</span></p>
        </div>`,
        primary: isPrologue ? 'Start' : `Read verse ${l.ch}.${l.from}`,
      };
    },
    card(step) {
      const c = step.card;
      let quote = '';
      if (c.verse) {
        const [vc, vv] = c.verse.split('.').map(Number);
        const d = cachedChapter(vc);
        const v = d && d.verseMap.get(vv);
        if (v) {
          quote = html`<figure class="card-step__quote">
            <blockquote class="card-step__quote-tr"><p>${v.translation}</p></blockquote>
            <figcaption class="card-step__quote-ref">Verse ${c.verse}</figcaption>
          </figure>`;
        }
      }
      const last = step.i === l.cards.length - 1;
      return {
        html: html`<div class="card-step">
          <p class="eyebrow">${step.i + 1} of ${l.cards.length}</p>
          <h2 class="h1 step__heading" tabindex="-1">${c.title}</h2>
          <div class="prose card-step__body">${paragraphs(c.body)}</div>
          ${c.points ? html`<ul class="points">${c.points.map((pt) => html`<li>${pt}</li>`)}</ul>` : ''}
          ${quote}
          ${c.people ? html`<div class="chips card-step__people">${c.people.map((pid) => {
            const p = person(pid);
            return p ? html`<button type="button" class="chip" data-person="${pid}"><span class="chip__deva" lang="sa">${p.sa}</span>${p.name}</button>` : '';
          })}</div>` : ''}
        </div>`,
        primary: last ? 'Quick check' : 'Next',
      };
    },
    verse(step) {
      const v = data.verseMap.get(step.v);
      const last = step.v === l.to;
      return {
        html: html`<div class="verse-step">
          <p class="eyebrow verse-step__eyebrow">Verse ${step.v - l.from + 1} of ${verseCount}</p>
          ${verseBlock(l.ch, v, { mode: 'lesson', headingLevel: 2 })}
        </div>`,
        primary: last ? 'Quick check' : 'Next verse',
        after() {
          actions.markVerse(`${l.ch}.${step.v}`);
        },
      };
    },
    quiz(step) {
      const q = step.q;
      const chosen = answers.get(step.i);
      const answered = chosen !== undefined;
      const last = step.i === quizTotal - 1;
      return {
        html: html`<div class="quiz">
          <p class="eyebrow">Quick check · ${step.i + 1} of ${quizTotal}</p>
          <h2 class="quiz__q step__heading" tabindex="-1" id="quiz-q">${q.q}</h2>
          <div class="quiz__options" role="group" aria-labelledby="quiz-q">
            ${q.options.map((o, i) => {
              const state = !answered ? '' : i === q.answer ? 'is-correct' : i === chosen ? 'is-wrong' : 'is-dim';
              return html`<button type="button" class="option ${state}" data-option="${i}" aria-disabled="${answered ? 'true' : 'false'}" aria-pressed="${chosen === i ? 'true' : 'false'}">
                <span class="option__key" aria-hidden="true">${answered && i === q.answer ? icon('check', { size: 16, stroke: 2.5 }) : answered && i === chosen ? icon('close', { size: 16, stroke: 2.5 }) : String.fromCharCode(65 + i)}</span>
                <span class="option__text">${o}</span>
              </button>`;
            })}
          </div>
          <div class="quiz__why" aria-live="polite">${answered ? html`<div class="why ${chosen === q.answer ? 'why--good' : 'why--bad'}">
            <p class="why__title">${chosen === q.answer ? 'Yes, that is right.' : 'Not quite.'}</p>
            <p>${q.why}</p></div>` : ''}</div>
        </div>`,
        primary: answered ? (last ? 'Continue' : 'Next question') : 'Choose an answer',
        disabled: !answered,
        bind(root) {
          for (const b of root.querySelectorAll('[data-option]')) {
            b.addEventListener('click', () => {
              if (answers.has(step.i)) return;
              answers.set(step.i, Number(b.dataset.option));
              render('none');
            });
          }
        },
      };
    },
    reflect() {
      const key = `r:${l.id}`;
      const existing = noteFor(key);
      return {
        html: html`<div class="reflect">
          <p class="eyebrow">Reflect</p>
          <h2 class="reflect__prompt step__heading" tabindex="-1" id="reflect-q">${extra.reflect}</h2>
          <label class="sr-only" for="reflect-text">Your reflection</label>
          <textarea id="reflect-text" class="textarea reflect__text" rows="6" maxlength="8000" placeholder="Write a sentence or two, or just think it over.">${existing ? existing.text : ''}</textarea>
          <p class="field__hint">${icon('lock', { size: 14 })} Private to you. Saved in Saved, under Notes.</p>
        </div>`,
        primary: 'Finish lesson',
        leave(root) {
          const ta = root.querySelector('#reflect-text');
          if (ta) actions.setNote(key, ta.value.trim());
        },
      };
    },
    done() {
      if (!completedNow) {
        completedNow = true;
        const correct = [...answers.entries()].filter(([i, a]) => extra.quiz[i].answer === a).length;
        actions.completeLesson(l.id, verseIdsForLesson(l));
        if (answers.size) actions.recordQuiz(l.id, correct, quizTotal);
        const n = nextLessonAfter(l.id, getProgress());
        const p = getProgress();
        if (n && !p.lessons[n.id]) actions.setCurrent(n.id, 0);
      }
      const p = getProgress();
      const s = chapterStats(p, l.ch);
      const correct = [...answers.entries()].filter(([i, a]) => extra.quiz[i].answer === a).length;
      const st = streaks(p.days);
      const n = nextLessonAfter(l.id, getProgress());
      const chapterDone = s.complete && l.isLast;
      const whole = overallStats(p);
      const partDone = whole.done === whole.total;
      return {
        html: html`<div class="done">
          <div class="done__scene">${dawnScene(Math.max(0.08, (l.index + 1) / idx.lessonList.length), { className: 'done__dawn' })}</div>
          <p class="eyebrow">${chapterDone ? (isPrologue ? 'Background complete' : `Chapter ${l.ch} complete`) : 'Lesson complete'}</p>
          <h2 class="display step__heading" tabindex="-1">${chapterDone && !isPrologue ? meta.title : l.title}</h2>
          ${saveCard({ level: 3, text: 'This lesson is saved on this device only. Sign in with your email to keep your progress safe and carry on from any phone or computer.' })}
          <figure class="done__takeaway">
            <figcaption class="eyebrow">Remember</figcaption>
            <blockquote><p>${l.takeaway}</p></blockquote>
          </figure>
          <dl class="done__stats">
            ${verseCount ? html`<div><dt>Verses read</dt><dd class="num">${verseCount}</dd></div>` : ''}
            ${answers.size ? html`<div><dt>Quick check</dt><dd class="num">${correct}/${quizTotal}</dd></div>` : ''}
            <div><dt>Day streak</dt><dd class="num">${st.current || 1}</dd></div>
          </dl>
          ${chapterDone && !isPrologue ? html`<div class="done__recap"><p class="eyebrow">What you now understand</p><p>${meta.recap}</p></div>` : ''}
          ${partDone ? html`<div class="done__recap"><p class="eyebrow">The whole Gita</p><p>You have read all 700 verses. Sanjaya's last words say that wherever Krishna and Arjuna stand together, there is fortune, victory and wisdom. Come back to any chapter whenever you need it.</p></div>` : ''}
          <div class="done__chapter">
            <p class="small"><strong>${isPrologue ? 'Background' : `Chapter ${l.ch}`}</strong> · ${s.done} of ${plural(s.total, 'lesson')}</p>
            <div class="bar" aria-hidden="true"><div class="bar__fill" style="width:${(s.done / s.total) * 100}%"></div></div>
          </div>
          ${n ? html`<p class="done__next small muted">Up next: <strong>${n.title}</strong>${n.ch !== l.ch ? html` · ${n.ch === 0 ? '' : `Chapter ${n.ch}`}` : ''}</p>` : ''}
        </div>`,
        primary: n ? (n.ch !== l.ch ? `Start chapter ${n.ch}` : 'Next lesson') : 'Back to Today',
        secondary: n ? 'Done for now' : '',
        bind: (stage) => bindSaveCard(stage),
      };
    },
  };

  // ---------- shell ----------
  const shell = html`<div class="player" style="--sky-top:${sky.top};--sky-bottom:${sky.bottom};--sky-ink:${sky.ink}">
    <header class="player__bar">
      <button type="button" class="icon-btn" data-exit aria-label="Close lesson">${icon('close', { size: 22 })}</button>
      <div class="player__track" role="progressbar" aria-label="Lesson progress" aria-valuemin="1" aria-valuemax="${steps.length}" aria-valuenow="${index + 1}">
        <div class="player__fill"></div>
      </div>
      <span class="player__count num" aria-hidden="true"></span>
    </header>
    <p class="sr-only" aria-live="polite" data-announce></p>
    <div class="player__stage"><div class="step" data-step></div></div>
    <footer class="player__foot">
      <div class="player__actions">
        <button type="button" class="icon-btn icon-btn--outline player__back" data-prev aria-label="Previous step">${icon('arrow-left', { size: 22 })}</button>
        <button type="button" class="btn btn--secondary player__secondary" data-secondary hidden></button>
        <button type="button" class="btn btn--primary player__primary" data-next></button>
      </div>
    </footer>
  </div>`;

  let root = null;
  let current = null;

  function render(direction) {
    const step = steps[index];
    current = R[step.type](step);
    const stage = root.querySelector('[data-step]');
    stage.innerHTML = String(current.html);
    stage.className = `step step--${step.type}${direction === 'forward' ? ' step--in-forward' : direction === 'back' ? ' step--in-back' : ''}`;
    if (current.bind) current.bind(stage);
    const primary = root.querySelector('[data-next]');
    primary.innerHTML = String(html`<span>${current.primary}</span>${step.type === 'done' ? '' : icon('arrow-right', { size: 20 })}`);
    primary.disabled = !!current.disabled;
    const secondary = root.querySelector('[data-secondary]');
    secondary.hidden = !current.secondary;
    secondary.textContent = current.secondary || '';
    root.querySelector('[data-prev]').hidden = index === 0 || step.type === 'done';
    const fill = root.querySelector('.player__fill');
    fill.style.width = `${(index / (steps.length - 1)) * 100}%`;
    root.querySelector('.player__track').setAttribute('aria-valuenow', String(index + 1));
    root.querySelector('.player__count').textContent = `${index + 1}/${steps.length}`;
    root.querySelector('[data-announce]').textContent = `Step ${index + 1} of ${steps.length}`;
    if (!wasComplete && step.type !== 'done' && !completedNow) actions.setCurrent(l.id, index);
    if (current.after) current.after();
    if (direction !== 'none') {
      window.scrollTo(0, 0);
      const h = stage.querySelector('.step__heading');
      if (h) h.focus({ preventScroll: true });
    }
    if (step.type === 'reflect') {
      // Keep the page usable when the on-screen keyboard opens.
      const ta = stage.querySelector('textarea');
      ta.addEventListener('focus', () => setTimeout(() => ta.scrollIntoView({ block: 'center', behavior: 'smooth' }), 250));
    }
  }

  function go(delta) {
    const nextIndex = index + delta;
    if (nextIndex < 0 || nextIndex >= steps.length) return;
    if (current && current.leave) current.leave(root);
    stopSpeech();
    index = nextIndex;
    render(delta > 0 ? 'forward' : 'back');
  }

  function primary() {
    const step = steps[index];
    if (current.disabled) return;
    if (step.type === 'done') {
      const n = nextLessonAfter(l.id, getProgress());
      navigate(n ? `/lesson/${n.id}` : '/', { replace: true });
      return;
    }
    go(1);
  }

  function exit() {
    const step = steps[index];
    if (current && current.leave) current.leave(root);
    stopSpeech();
    if (!completedNow && step.type !== 'intro' && step.type !== 'opening' && !wasComplete) {
      toast('Progress saved. Pick up from Today whenever you like.');
    }
    back('/');
  }

  return {
    title: l.title,
    focus: true,
    html: shell,
    mount(el) {
      root = el.querySelector('.player');
      render('none');
      const h = root.querySelector('.step__heading');
      if (h) setTimeout(() => h.focus({ preventScroll: true }), 30);
      root.querySelector('[data-next]').addEventListener('click', primary);
      root.querySelector('[data-prev]').addEventListener('click', () => go(-1));
      root.querySelector('[data-exit]').addEventListener('click', exit);
      root.querySelector('[data-secondary]').addEventListener('click', () => navigate('/', { replace: true }));
      root.addEventListener('click', (e) => {
        const p = e.target.closest('[data-person]');
        if (p) openPersonSheet(p.dataset.person);
      });

      const onKey = (e) => {
        if (document.querySelector('dialog[open]')) return;
        const typing = e.target.closest('textarea, input');
        if (e.key === 'Escape' && !typing) {
          e.preventDefault();
          exit();
        } else if (e.key === 'ArrowRight' && !typing) {
          e.preventDefault();
          primary();
        } else if (e.key === 'ArrowLeft' && !typing && index > 0 && steps[index].type !== 'done') {
          e.preventDefault();
          go(-1);
        } else if (steps[index].type === 'quiz' && !typing && /^[1-4a-dA-D]$/.test(e.key)) {
          const n = /[1-4]/.test(e.key) ? Number(e.key) - 1 : e.key.toLowerCase().charCodeAt(0) - 97;
          const b = root.querySelector(`[data-option="${n}"]`);
          if (b) b.click();
        }
      };
      document.addEventListener('keydown', onKey);

      // Swipe between steps on touch screens.
      let sx = 0;
      let sy = 0;
      let tracking = false;
      const stage = root.querySelector('.player__stage');
      const onStart = (e) => {
        if (e.touches.length !== 1 || e.target.closest('textarea, input, .option, button, a')) return;
        tracking = true;
        sx = e.touches[0].clientX;
        sy = e.touches[0].clientY;
      };
      const onEnd = (e) => {
        if (!tracking) return;
        tracking = false;
        const t = e.changedTouches[0];
        const dx = t.clientX - sx;
        const dy = t.clientY - sy;
        if (Math.abs(dx) < 70 || Math.abs(dy) > 50) return;
        if (window.getSelection && String(window.getSelection())) return;
        if (dx < 0) primary();
        else if (index > 0 && steps[index].type !== 'done') go(-1);
      };
      stage.addEventListener('touchstart', onStart, { passive: true });
      stage.addEventListener('touchend', onEnd, { passive: true });

      return () => {
        if (current && current.leave && root.isConnected) current.leave(root);
        document.removeEventListener('keydown', onKey);
        stopSpeech();
      };
    },
  };
}
